// IND-049: guardado de las casillas de la Hoja de recuento. Cada casilla se
// guarda al salir de ella, de una en una y en orden: si se escribe otra vez
// mientras se guarda, se envía después solo lo último (nada se pierde ni se
// duplica). Sin conexión, lo escrito espera y se reintenta; si PROA lo
// rechaza, la casilla se queda con lo tecleado.
import type { RecountRowSource } from '../services/industrialCostService';

export type RecountCellField = 'current_qty' | 'purchases_qty' | 'price';
export type RecountCellStatus = 'saving' | 'saved' | 'error' | 'invalid' | 'offline';
export type RecountCellState = { status: RecountCellStatus; text: string };
export type RecountCellItem = {
  rowId: string;
  field: RecountCellField;
  value: number | null;
  source: RecountRowSource;
  /** Lo tecleado, para enseñarlo mientras se guarda o si falla. */
  text: string;
};

type Listener<T> = {
  onState: (key: string, state: RecountCellState | null) => void;
  onSaved: (saved: T, item: RecountCellItem) => void;
};

const LIMITS: Record<RecountCellField, [number, number]> = {
  current_qty: [0, 1e9],
  purchases_qty: [-1e9, 1e9],
  price: [0, 1e6],
};

/** Lo tecleado en formato español («1.234,5» o «1234,5»). Vacío = null; si no
 * es un número válido para esa columna, «invalid». */
export function parseCellText(text: string, field: RecountCellField): number | null | 'invalid' {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^-?[\d.,]+$/.test(trimmed)) return 'invalid';
  const plain = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(trimmed) ? trimmed.replace(/\./g, '') : trimmed;
  const value = Number(plain.replace(',', '.'));
  const [low, high] = LIMITS[field];
  return Number.isFinite(value) && value >= low && value <= high ? value : 'invalid';
}

export function cellKey(rowId: string, field: RecountCellField): string {
  return `${rowId}|${field}`;
}

function isOffline(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  // Sin respuesta o fallo del servidor: se puede reintentar.
  return status === undefined || status >= 500;
}

export class RecountCellSaver<T> {
  private readonly states = new Map<string, RecountCellState>();
  private readonly running = new Set<string>();
  private readonly next = new Map<string, RecountCellItem>();
  private readonly waiting = new Map<string, RecountCellItem>();
  private readonly tokens = new Map<string, number>();

  constructor(
    private readonly send: (item: RecountCellItem) => Promise<T>,
    private readonly listener: Listener<T>,
    private readonly savedMs = 1200,
  ) {}

  state(key: string): RecountCellState | undefined {
    return this.states.get(key);
  }

  /** Esa casilla tiene un guardado en marcha o esperando. */
  busy(rowId: string, field: RecountCellField): boolean {
    const key = cellKey(rowId, field);
    return this.running.has(key) || this.next.has(key) || this.waiting.has(key);
  }

  /** Algo escrito que aún no está en PROA. */
  hasUnsaved(): boolean {
    return [...this.states.values()].some((state) => state.status !== 'saved');
  }

  save(item: RecountCellItem): void {
    const key = cellKey(item.rowId, item.field);
    this.waiting.delete(key);
    this.setState(key, { status: 'saving', text: item.text });
    if (this.running.has(key)) {
      this.next.set(key, item);
      return;
    }
    void this.run(key, item);
  }

  /** No es un número: no se envía y la casilla se queda con lo tecleado. */
  invalid(rowId: string, field: RecountCellField, text: string): void {
    const key = cellKey(rowId, field);
    this.waiting.delete(key);
    this.next.delete(key);
    this.setState(key, { status: 'invalid', text });
  }

  /** Esc o volver al valor guardado: se olvida lo pendiente de esa casilla. */
  discard(rowId: string, field: RecountCellField): void {
    const key = cellKey(rowId, field);
    this.waiting.delete(key);
    this.next.delete(key);
    if (!this.running.has(key)) this.setState(key, null);
  }

  retry(rowId: string, field: RecountCellField): void {
    const item = this.waiting.get(cellKey(rowId, field));
    if (item) this.save(item);
  }

  /** Al volver la conexión: lo que esperaba sin conexión. */
  retryOffline(): void {
    for (const [key, item] of [...this.waiting]) {
      if (this.states.get(key)?.status === 'offline') this.save(item);
    }
  }

  private setState(key: string, state: RecountCellState | null): void {
    this.tokens.set(key, (this.tokens.get(key) ?? 0) + 1);
    if (state) this.states.set(key, state);
    else this.states.delete(key);
    this.listener.onState(key, state);
  }

  private async run(key: string, item: RecountCellItem): Promise<void> {
    this.running.add(key);
    let failure: unknown = null;
    try {
      const saved = await this.send(item);
      this.listener.onSaved(saved, item);
    } catch (error) {
      failure = error ?? new Error('save failed');
    } finally {
      this.running.delete(key);
    }
    const following = this.next.get(key);
    if (following) {
      // Lo último escrito manda sobre lo que se estaba guardando.
      this.next.delete(key);
      void this.run(key, following);
      return;
    }
    if (failure !== null) {
      this.waiting.set(key, item);
      this.setState(key, { status: isOffline(failure) ? 'offline' : 'error', text: item.text });
      return;
    }
    this.setState(key, { status: 'saved', text: item.text });
    const token = this.tokens.get(key);
    setTimeout(() => {
      if (this.tokens.get(key) === token && this.states.get(key)?.status === 'saved') this.setState(key, null);
    }, this.savedMs);
  }
}
